import type {
    EnvironmentalChartPoint,
    EnvironmentalMonthlyChartPoint,
    EnvironmentalStressMonitoringData,
} from './environmentalStressMonitoring.model';

const chartPoint = (date: string, timestamp: string, values: readonly number[]): EnvironmentalChartPoint => ({
    date,
    timestamp,
    temperature: values[0],
    humidity: values[1],
    emc: values[2],
    dewPoint: values[3],
    dewPointMargin: values[4],
});

const monthlyPoint = (month: string, values: readonly number[]): EnvironmentalMonthlyChartPoint => ({
    month,
    temperature: values[0],
    humidity: values[1],
    emc: values[2],
    dewPoint: values[3],
    dewPointMargin: values[4],
});

const chartPoints: EnvironmentalChartPoint[] = [
    chartPoint('09/01/21', '8:00 AM', [30, 63, 7, 17, 20]),
    chartPoint('09/08/21', '8:00 AM', [31, 65, 8, 17, 20]),
    chartPoint('09/15/21', '8:00 AM', [32, 66, 8, 18, 19]),
    chartPoint('09/22/21', '8:00 AM', [33, 67, 9, 18, 19]),
    chartPoint('09/29/21', '8:00 AM', [34, 68, 9, 18, 19]),
    chartPoint('10/06/21', '8:00 AM', [35, 69, 9, 18, 19]),
    chartPoint('10/13/21', '8:00 AM', [36, 71, 10, 19, 18]),
    chartPoint('10/20/21', '8:00 AM', [37, 72, 10, 19, 18]),
    chartPoint('10/27/21', '8:00 AM', [38, 73, 10, 19, 18]),
    chartPoint('11/03/21', '8:00 AM', [39, 74, 11, 20, 17]),
    chartPoint('11/10/21', '8:00 AM', [40, 76, 11, 20, 17]),
    chartPoint('11/17/21', '8:00 AM', [39, 75, 10, 19, 18]),
    chartPoint('11/24/21', '8:00 AM', [37, 75, 8, 18, 19]),
];

const monthlyChartPoints = [
    ['Sep 2021', [33, 66, 7, 17, 20]],
    ['Oct 2021', [36, 71, 9, 18, 19]],
    ['Nov 2021', [38, 75, 10, 19, 18]],
    ['Dec 2021', [37, 73, 9, 19, 18]],
    ['Jan 2022', [35, 70, 9, 18, 19]],
    ['Feb 2022', [34, 68, 8, 18, 19]],
    ['Mar 2022', [32, 66, 8, 17, 20]],
    ['Apr 2022', [31, 65, 7, 17, 20]],
    ['May 2022', [32, 66, 7, 17, 20]],
    ['Jun 2022', [33, 67, 8, 18, 19]],
    ['Jul 2022', [35, 70, 9, 18, 19]],
    ['Aug 2022', [36, 72, 9, 19, 18]],
] as const;

const range24Hours = [
    chartPoint('11/24/21', '12:00 AM', [34, 71, 8, 17, 21]),
    chartPoint('11/24/21', '4:00 AM', [33, 70, 8, 17, 21]),
    chartPoint('11/24/21', '8:00 AM', [36, 73, 9, 18, 20]),
    chartPoint('11/24/21', '12:00 PM', [39, 76, 10, 20, 18]),
    chartPoint('11/24/21', '4:00 PM', [40, 77, 11, 21, 17]),
    chartPoint('11/24/21', '8:00 PM', [37, 75, 9, 19, 19]),
];

const range15Minutes = [
    chartPoint('11/24/21', '8:00 AM', [36, 73, 9, 18, 20]),
    chartPoint('11/24/21', '8:03 AM', [37, 74, 9, 18, 20]),
    chartPoint('11/24/21', '8:06 AM', [37, 74, 9, 18, 20]),
    chartPoint('11/24/21', '8:09 AM', [38, 75, 10, 19, 19]),
    chartPoint('11/24/21', '8:12 AM', [37, 75, 9, 19, 19]),
    chartPoint('11/24/21', '8:15 AM', [37, 75, 9, 19, 19]),
];

const range7Days = [
    chartPoint('11/18/21', '8:00 AM', [38, 74, 10, 19, 18]),
    chartPoint('11/19/21', '8:00 AM', [39, 75, 10, 20, 18]),
    chartPoint('11/20/21', '8:00 AM', [40, 76, 11, 20, 17]),
    chartPoint('11/21/21', '8:00 AM', [39, 76, 11, 20, 17]),
    chartPoint('11/22/21', '8:00 AM', [38, 75, 10, 19, 18]),
    chartPoint('11/23/21', '8:00 AM', [37, 75, 9, 19, 19]),
    chartPoint('11/24/21', '8:00 AM', [37, 75, 8, 18, 19]),
];

const range1Month = chartPoints.slice(-4);
const range3Months = chartPoints;

export const MOCK_ENVIRONMENTAL_STRESS_MONITORING: EnvironmentalStressMonitoringData = {
    metrics: [
        {
            id: 'temperature',
            label: 'TEMPERATURE',
            value: '37',
            unit: 'C',
            status: 'warning',
            statusLabel: '1 in Warning Range',
            warningThreshold: 35,
            alarmThreshold: 40,
        },
        {
            id: 'humidity',
            label: 'HUMIDITY',
            value: '75',
            unit: '%',
            status: 'alarm',
            statusLabel: '2 in Alarm Range',
            warningThreshold: 70,
            alarmThreshold: 75,
        },
        {
            id: 'emc',
            label: 'EMC',
            value: '8',
            unit: '%',
            status: 'expected',
            statusLabel: 'In Expected Range',
            warningThreshold: 12,
            alarmThreshold: 15,
        },
        {
            id: 'dew-point',
            label: 'DEW POINT',
            value: '18',
            unit: 'C',
            status: 'expected',
            statusLabel: 'In Expected Range',
            warningThreshold: 25,
            alarmThreshold: 30,
        },
        {
            id: 'dew-point-margin',
            label: 'DEW POINT MARGIN',
            value: '19',
            unit: 'C',
            status: 'expected',
            statusLabel: 'In Expected Range',
            warningThreshold: 25,
            alarmThreshold: 30,
        },
    ],
    notifications: [
        {
            id: 'humidity-1',
            metricId: 'humidity',
            title: 'High Humidity',
            severity: 'alarm',
            value: '72',
            unit: '%',
            time: '8:21 AM',
            date: '11/23/21',
            message: 'Threshold exceeded',
            recommendation:
                'High humidity can indicate that the space needs better ventilation. Review the ventilation and reduce humidity in the monitored area.',
            eventDetails: { average: '69%', integrated: '1980 %RH Days', threshold: '1950' },
        },
        {
            id: 'humidity-2',
            metricId: 'humidity',
            title: 'High Humidity',
            severity: 'alarm',
            value: '75',
            unit: '%',
            time: '8:21 AM',
            date: '11/23/21',
            message: 'Threshold exceeded',
            recommendation:
                'High humidity can indicate that the space needs better ventilation. Review the ventilation and reduce humidity in the monitored area.',
            eventDetails: { average: '66%', integrated: '1950 %RH Days', threshold: '1950' },
        },
        {
            id: 'temperature-1',
            metricId: 'temperature',
            title: 'High Temperature',
            severity: 'warning',
            value: '37',
            unit: 'C',
            time: '8:21 AM',
            date: '11/23/21',
            message: 'Threshold exceeded',
            recommendation:
                'High temperature can affect equipment health. Check cooling equipment and reduce the temperature in the monitored area.',
            eventDetails: { average: '37°C', integrated: '888 °C Days', threshold: '850' },
        },
    ],
    chartPoints,
    chartPointsByRange: {
        '15-minutes': range15Minutes,
        '1-day': range24Hours,
        '1-week': range7Days,
        '1-month': range1Month,
        '3-months': range3Months,
    },
    monthlyChartPoints: monthlyChartPoints.map(([month, values]) => monthlyPoint(month, values)),
    timeRanges: [
        { id: '15-minutes', label: '15 Minutes' },
        { id: '1-day', label: '1 Day' },
        { id: '1-week', label: '1 Week' },
        { id: '1-month', label: '1 Month' },
        { id: '3-months', label: '3 Months' },
    ],
    notificationHistory: [
        {
            id: 'history-temperature',
            metricId: 'temperature',
            severity: 'warning',
            title: 'Remote Temperature',
            time: '9:52 AM',
            date: '11/23/21',
            description: 'Temperature rose above the warning threshold of 35C.',
            message: 'Threshold exceeded',
        },
        {
            id: 'history-humidity',
            metricId: 'humidity',
            severity: 'alarm',
            title: 'Remote Humidity',
            time: '9:40 AM',
            date: '11/22/21',
            description: 'Humidity rose above the alarm threshold of 75%.',
            message: 'Threshold exceeded',
        },
        {
            id: 'history-emc',
            metricId: 'emc',
            severity: 'normal',
            title: 'EMC',
            time: '8:15 AM',
            date: '11/22/21',
            message: 'Threshold exceeded',
        },
        {
            id: 'history-dew-point',
            metricId: 'dew-point',
            severity: 'offline',
            title: 'Dew Point',
            time: '7:02 AM',
            date: '11/21/21',
            description: 'Sensor connection lost.',
            message: 'Threshold exceeded',
        },
    ],
    rules: {
        samplingIntervalMinutes: 15,
        notificationTriggerConsecutivePoints: 10,
        severityMapping: { warning: 'warning', alarm: 'critical' },
    },
};
